import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useClients } from '@/features/clients/hooks/useClients'
import { usePayments } from '@/features/payments/hooks/usePayments'
import { useInvoices } from '@/features/invoices/hooks/useInvoices'
import { PaymentDetailModal } from '@/features/payments/components/PaymentDetailModal'
import { getMonthStart, getMonthEnd, getCurrentMonthYear } from '@/lib/date-strings'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { DollarSign, Briefcase, TrendingUp, PiggyBank } from 'lucide-react'
import { formatCurrency, formatDate } from '@/lib/format'

export function DashboardPage() {
  const navigate = useNavigate()
  const { data: clients } = useClients()
  const { data: payments } = usePayments()
  const { data: invoices } = useInvoices()
  const [showBaselineModal, setShowBaselineModal] = useState(false)
  const [showCurrentARModal, setShowCurrentARModal] = useState(false)
  const [showMonthCollectionsModal, setShowMonthCollectionsModal] = useState(false)
  const [showYearCollectionsModal, setShowYearCollectionsModal] = useState(false)
  const [showMonthFeesModal, setShowMonthFeesModal] = useState(false)
  const [showYearFeesModal, setShowYearFeesModal] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState<any>(null)

  const stats = useMemo(() => {
    const activeClients = clients?.filter(c => c.status === 'active') || []
    const totalAR = clients?.reduce((sum, c) => sum + c.baseline_ar_total, 0) || 0

    // Calculate current AR from all invoice balances
    const currentAR = invoices?.reduce((sum: number, inv: any) => sum + (inv.balance_due || 0), 0) || 0

    // Current AR by client
    const currentARByClient = invoices?.reduce((acc: any, inv: any) => {
      const clientId = inv.client_id
      if (!clientId) return acc
      if (!acc[clientId]) {
        const client = clients?.find(c => c.id === clientId)
        acc[clientId] = { name: client?.name || 'Unknown', amount: 0 }
      }
      acc[clientId].amount += inv.balance_due || 0
      return acc
    }, {}) || {}

    // Use string comparison to avoid timezone bugs
    const { year, month } = getCurrentMonthYear()
    const monthStart = getMonthStart(year, month)
    const monthEnd = getMonthEnd(year, month)
    const yearStart = `${year}-01-01`
    const yearEnd = `${year}-12-31`

    const thisMonthPayments = payments?.filter(p =>
      p.payment_date >= monthStart && p.payment_date <= monthEnd
    ) || []

    const thisYearPayments = payments?.filter(p =>
      p.payment_date >= yearStart && p.payment_date <= yearEnd
    ) || []
    
    const collectionsThisMonth = thisMonthPayments.reduce((sum, p) => sum + p.amount, 0)
    const collectionsThisYear = thisYearPayments.reduce((sum, p) => sum + p.amount, 0)
    const feesThisMonth = thisMonthPayments.reduce((sum, p) => sum + p.collection_fee_owed, 0)
    const feesThisYear = thisYearPayments.reduce((sum, p) => sum + p.collection_fee_owed, 0)
    
    // Collections by client for the year
    const yearCollectionsByClient = thisYearPayments.reduce((acc: any, p) => {
      const clientName = (p as any).client?.name || 'Unknown'
      if (!acc[clientName]) acc[clientName] = 0
      acc[clientName] += p.amount
      return acc
    }, {})

    // Fees by client for month and year
    const monthFeesByClient = thisMonthPayments.reduce((acc: any, p) => {
      const clientName = (p as any).client?.name || 'Unknown'
      if (!acc[clientName]) acc[clientName] = 0
      acc[clientName] += p.collection_fee_owed
      return acc
    }, {})

    const yearFeesByClient = thisYearPayments.reduce((acc: any, p) => {
      const clientName = (p as any).client?.name || 'Unknown'
      if (!acc[clientName]) acc[clientName] = 0
      acc[clientName] += p.collection_fee_owed
      return acc
    }, {})
    
    return {
      totalAR,
      currentAR,
      currentARByClient,
      collectionsThisMonth,
      collectionsThisYear,
      feesThisMonth,
      feesThisYear,
      activeClients,
      thisMonthPayments,
      yearCollectionsByClient,
      monthFeesByClient,
      yearFeesByClient,
    }
  }, [clients, payments, invoices])

  const StatBox = ({ 
    label, 
    value, 
    icon: Icon, 
    iconColor, 
    onClick 
  }: { 
    label: string
    value: string | number
    icon: any
    iconColor: string
    onClick?: () => void
  }) => (
    <Card 
      className={onClick ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <Icon className={`h-10 w-10 ${iconColor}`} />
      </div>
    </Card>
  )

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <StatBox
          label="Current AR"
          value={formatCurrency(stats.currentAR)}
          icon={DollarSign}
          iconColor="text-primary-600"
          onClick={() => setShowCurrentARModal(true)}
        />

        <StatBox
          label="Total Baseline AR"
          value={formatCurrency(stats.totalAR)}
          icon={DollarSign}
          iconColor="text-gray-600"
          onClick={() => setShowBaselineModal(true)}
        />

        <StatBox
          label="Collections This Month"
          value={formatCurrency(stats.collectionsThisMonth)}
          icon={TrendingUp}
          iconColor="text-success-600"
          onClick={() => setShowMonthCollectionsModal(true)}
        />

        <StatBox
          label="Collections This Year"
          value={formatCurrency(stats.collectionsThisYear)}
          icon={DollarSign}
          iconColor="text-success-600"
          onClick={() => setShowYearCollectionsModal(true)}
        />

        <StatBox
          label="Fees Earned This Month"
          value={formatCurrency(stats.feesThisMonth)}
          icon={PiggyBank}
          iconColor="text-warning-600"
          onClick={() => setShowMonthFeesModal(true)}
        />

        <StatBox
          label="Fees Earned This Year"
          value={formatCurrency(stats.feesThisYear)}
          icon={PiggyBank}
          iconColor="text-warning-600"
          onClick={() => setShowYearFeesModal(true)}
        />

        <StatBox
          label="Active Clients"
          value={stats.activeClients.length}
          icon={Briefcase}
          iconColor="text-primary-600"
          onClick={() => navigate('/clients')}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Clients</h2>
          {clients && clients.length > 0 ? (
            <div className="space-y-3">
              {clients.slice(0, 5).map(client => (
                <div
                  key={client.id}
                  onClick={() => navigate(`/clients/${client.id}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/clients/${client.id}`)}
                  className="flex items-center justify-between py-2 border-b last:border-0 cursor-pointer hover:bg-gray-50 rounded px-2 -mx-2 transition-colors"
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${client.name} details`}
                >
                  <div>
                    <p className="font-medium text-gray-900">{client.name}</p>
                    <p className="text-sm text-gray-500">{client.status}</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {formatCurrency(client.baseline_ar_total)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-center py-4">No clients yet.</p>
          )}
        </Card>

        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Payments</h2>
          {payments && payments.length > 0 ? (
            <div className="space-y-3">
              {payments.slice(0, 5).map(payment => (
                <div 
                  key={payment.id} 
                  className="flex items-center justify-between py-2 border-b last:border-0 cursor-pointer hover:bg-gray-50 rounded px-2 -mx-2 transition-colors"
                  onClick={() => setSelectedPayment(payment)}
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{formatCurrency(payment.amount)}</p>
                    <p className="text-sm text-gray-500">
                      {(payment as any).client?.name} • {(payment as any).customer?.name}
                    </p>
                    <p className="text-xs text-gray-400">{formatDate(payment.payment_date)}</p>
                  </div>
                  <p className="text-sm font-medium text-success-600">
                    Fee: {formatCurrency(payment.collection_fee_owed)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-center py-4">No payments yet.</p>
          )}
        </Card>
      </div>

      {/* Current AR Modal */}
      <Modal
        isOpen={showCurrentARModal}
        onClose={() => setShowCurrentARModal(false)}
        title="Current AR"
        size="md"
      >
        <div className="space-y-3">
          {Object.entries(stats.currentARByClient)
            .sort(([, a]: any, [, b]: any) => b.amount - a.amount)
            .map(([clientId, data]: any) => (
              <div key={clientId} className="flex items-center justify-between py-3 border-b last:border-b-0">
                <span className="text-sm font-medium text-gray-900">{data.name}</span>
                <span className="text-sm font-semibold text-primary-600">
                  {formatCurrency(data.amount)}
                </span>
              </div>
            ))}
          <div className="flex items-center justify-between pt-3 border-t-2">
            <span className="text-base font-bold text-gray-900">Total</span>
            <span className="text-base font-bold text-primary-600">
              {formatCurrency(stats.currentAR)}
            </span>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <Button variant="secondary" onClick={() => setShowCurrentARModal(false)}>Close</Button>
        </div>
      </Modal>

      {/* Baseline AR Modal */}
      <Modal
        isOpen={showBaselineModal}
        onClose={() => setShowBaselineModal(false)}
        title="Total Baseline AR"
        size="md"
      >
        <div className="space-y-3">
          {stats.activeClients.map((client: any) => (
            <div key={client.id} className="flex items-center justify-between py-3 border-b last:border-b-0">
              <span className="text-sm font-medium text-gray-900">{client.name}</span>
              <span className="text-sm font-semibold text-gray-900">
                {formatCurrency(client.baseline_ar_total)}
              </span>
            </div>
          ))}
          <div className="flex items-center justify-between pt-3 border-t-2">
            <span className="text-base font-bold text-gray-900">Total</span>
            <span className="text-base font-bold text-gray-900">
              {formatCurrency(stats.totalAR)}
            </span>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <Button variant="secondary" onClick={() => setShowBaselineModal(false)}>Close</Button>
        </div>
      </Modal>

      {/* Collections This Month Modal */}
      <Modal 
        isOpen={showMonthCollectionsModal} 
        onClose={() => setShowMonthCollectionsModal(false)} 
        title="Collections This Month"
        size="lg"
      >
        <div className="space-y-2">
          {stats.thisMonthPayments.map((payment: any) => (
            <div key={payment.id} className="flex items-center justify-between py-3 border-b last:border-b-0">
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">{(payment as any).client?.name}</span>
                <p className="text-xs text-gray-500">{formatDate(payment.payment_date)}</p>
              </div>
              <span className="text-sm font-semibold text-success-600">
                {formatCurrency(payment.amount)}
              </span>
            </div>
          ))}
          <div className="flex items-center justify-between pt-3 border-t-2">
            <span className="text-base font-bold text-gray-900">Total</span>
            <span className="text-base font-bold text-success-600">
              {formatCurrency(stats.collectionsThisMonth)}
            </span>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <Button variant="secondary" onClick={() => setShowMonthCollectionsModal(false)}>Close</Button>
        </div>
      </Modal>

      {/* Collections This Year Modal */}
      <Modal 
        isOpen={showYearCollectionsModal} 
        onClose={() => setShowYearCollectionsModal(false)} 
        title="Collections This Year"
        size="md"
      >
        <div className="space-y-3">
          {Object.entries(stats.yearCollectionsByClient)
            .sort(([, a]: any, [, b]: any) => b - a)
            .map(([clientName, amount]: any) => (
              <div key={clientName} className="flex items-center justify-between py-3 border-b last:border-b-0">
                <span className="text-sm font-medium text-gray-900">{clientName}</span>
                <span className="text-sm font-semibold text-success-600">
                  {formatCurrency(amount)} Collected
                </span>
              </div>
            ))}
          <div className="flex items-center justify-between pt-3 border-t-2">
            <span className="text-base font-bold text-gray-900">Total</span>
            <span className="text-base font-bold text-success-600">
              {formatCurrency(stats.collectionsThisYear)}
            </span>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <Button variant="secondary" onClick={() => setShowYearCollectionsModal(false)}>Close</Button>
        </div>
      </Modal>

      {/* Fees This Month Modal */}
      <Modal 
        isOpen={showMonthFeesModal} 
        onClose={() => setShowMonthFeesModal(false)} 
        title="Fees Earned This Month"
        size="md"
      >
        <div className="space-y-3">
          {Object.entries(stats.monthFeesByClient)
            .sort(([, a]: any, [, b]: any) => b - a)
            .map(([clientName, amount]: any) => (
              <div key={clientName} className="flex items-center justify-between py-3 border-b last:border-b-0">
                <span className="text-sm font-medium text-gray-900">{clientName}</span>
                <span className="text-sm font-semibold text-warning-600">
                  {formatCurrency(amount)}
                </span>
              </div>
            ))}
          <div className="flex items-center justify-between pt-3 border-t-2">
            <span className="text-base font-bold text-gray-900">Total</span>
            <span className="text-base font-bold text-warning-600">
              {formatCurrency(stats.feesThisMonth)}
            </span>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <Button variant="secondary" onClick={() => setShowMonthFeesModal(false)}>Close</Button>
        </div>
      </Modal>

      {/* Fees This Year Modal */}
      <Modal 
        isOpen={showYearFeesModal} 
        onClose={() => setShowYearFeesModal(false)} 
        title="Fees Earned This Year"
        size="md"
      >
        <div className="space-y-3">
          {Object.entries(stats.yearFeesByClient)
            .sort(([, a]: any, [, b]: any) => b - a)
            .map(([clientName, amount]: any) => (
              <div key={clientName} className="flex items-center justify-between py-3 border-b last:border-b-0">
                <span className="text-sm font-medium text-gray-900">{clientName}</span>
                <span className="text-sm font-semibold text-warning-600">
                  {formatCurrency(amount)}
                </span>
              </div>
            ))}
          <div className="flex items-center justify-between pt-3 border-t-2">
            <span className="text-base font-bold text-gray-900">Total</span>
            <span className="text-base font-bold text-warning-600">
              {formatCurrency(stats.feesThisYear)}
            </span>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <Button variant="secondary" onClick={() => setShowYearFeesModal(false)}>Close</Button>
        </div>
      </Modal>

      {/* Payment Detail Modal */}
      {selectedPayment && (
        <PaymentDetailModal
          payment={selectedPayment}
          isOpen={!!selectedPayment}
          onClose={() => setSelectedPayment(null)}
        />
      )}
    </div>
  )
}
